import { EventEmitter, Injectable, OnDestroy, Optional } from '@angular/core';
import { HubAction } from 'enums/HubAction';
import { ModelProperty } from 'models/Model';
import { Subject, Subscription } from 'rxjs';
import { IPushMessage } from 'services/push.service';

@Injectable() export class RefreshService {

	private _refreshable = false;

	public onRefresh = new Subject<void>();

	get refreshable() {
		return this._refreshable;
	}

	refresh() {
		if (!this._refreshable) return;
		this.onRefresh.next();
		this._refreshable = false;
	}

	refreshAvailable() {
		this._refreshable = true;
	}

}

/**
 * Ensures data tracking subscriptions are cleaned up when a component is destroyed.
 * Required to track data models for components.
 */
@Injectable() export class DataTracking implements OnDestroy, TrackingContext {

	private destroyEvent = new EventEmitter<void>();
	public onDestroy(next?: (value: void) => void) { return this.destroyEvent.subscribe(next); }
	refreshService?: RefreshService;

	constructor(@Optional() refreshService: RefreshService) {
		this.refreshService = refreshService;
	}

	ngOnDestroy() {
		this.destroyEvent.emit();
	}
}

export interface TrackingContext { onDestroy: (next?: (value: void) => void) => void, refreshService?: RefreshService }
export interface InsertFilter<T> { (object: T): boolean }

class TrackedDataMap<T, TMessageData> {
	public context: TrackingContext;
	public objectMap = new Map<string, T[]>();
	public trackedRefreshableArrays: { objects: T[], filterForInserts: (data: TMessageData) => boolean, refreshService: RefreshService, refreshSubscription: Subscription }[] = [];
	constructor(context: TrackingContext) {
		this.context = context;
	}
}

/**
 * Tracks data models for components with live data, and automatically cleans up destroyed components, preventing unnecessary backend calls.
 * 
 * Calling trackObjectForComponent will add the provided component as a tracker for an object.
 * Calling refreshData will update the data for all components that are tracking the model's identifier.
 */
export class LiveDataTracker<T extends { getProperties: () => ModelProperty[], $deleted?: boolean, $new?: boolean }, TMessageData extends Object> {

	private maps: TrackedDataMap<T, TMessageData>[] = [];
	private revealedObjectIds: string[] = [];
	
	constructor(private getIdentifier: (object: T) => string) { }

	/**
	 * Updates all tracked objects that match the provided identifier.
	 * @param event Event containing information about the action performed, the user who performed it, and data describing the affected object.
	 * @param identifier The identifier to match.
	 * @param loadObjectData Async callback used to obtain new data for the object if it is being tracked.
	 * @param callback this optional async callback can handle additional by the calling service.
	 * @example refreshData(event, message.data.userId, () => this.userService.getUser(message.data.userId), async (newUser, context) => { this.exampleTracker.trackArrayForComponent(context, newUser.examples) }; });
	 */
	public async refreshData(event: IPushMessage<TMessageData>, identifier: string, loadObjectData: () => Promise<T>, callback?: (dataObject: T, trackingContext: TrackingContext) => Promise<void>) {

		if (event.action == HubAction.Insert) {

			if (!event.data) return;

			// If no components have an array with a matching filter, no need to insert the object
			if (!this.maps.some(map => map.trackedRefreshableArrays.some(array => event.data && array.filterForInserts(event.data)))) return;

			const objectData = await loadObjectData();
			this.insertModels(objectData, event.data, event.fromCurrentUser, callback);
		}
		else {
			
			// If no components are tracking the object, no need to update or delete it
			if (!this.maps.some(map => map.objectMap.has(identifier))) return;

			if (event.action == HubAction.Delete) this.removeMatchingModels(identifier, event.fromCurrentUser);
			if (event.action == HubAction.Update) {
				const objectData = await loadObjectData();
				this.updateMatchingModels(identifier, objectData, callback);
			}
		}
	}

	/**
	 * Helper function to track all objects in an array for a given TrackingContext.
	 * @param context TrackingContext to track the object for.
	 * @param objects The objects to track.
	 * @param filterForInserts Function used to identify if new objects should be included for tracked array on later updates.
	 * @returns The provided objects for chaining.
	 * @example trackArrayForComponent(context, this.users);
	 */
	public trackArrayForComponent(context: TrackingContext | undefined, objects: T[], filterForInserts?: (object: TMessageData) => boolean) {

		if (!context)
			return objects;

		for (const object of objects) this.trackObjectForComponent(context, object);
		
		if (!filterForInserts || !context.refreshService)
			return objects;

		const map = this.getMap(context);
		const refreshSubscription = context.refreshService.onRefresh.subscribe(() => {
			while (objects.some(obj => obj.$deleted)) {
				let objIndex = objects.findIndex(obj => obj.$deleted)
				objects.splice(objIndex, 1);
			}
			objects.filter(obj => obj.$new).forEach(obj => obj.$new = false);
		});
		map.trackedRefreshableArrays.push({ objects, filterForInserts, refreshService: context.refreshService, refreshSubscription });
		
		return objects;
	}

	/**
	 * Tracks the provided object for the provided component by the provided identifier.
	 * @param context TrackingContext to track the object for.
	 * @param object The object to track.
	 * @example trackArrayForComponent(context, user);
	 */
	public trackObjectForComponent(context: TrackingContext | undefined, object: T) {

		if (!context) return object;
		const identifier = this.getIdentifier(object);

		let map = this.getMap(context);
		if (!map.objectMap.has(identifier)) {
			map.objectMap.set(identifier, []);
		}
		map.objectMap.get(identifier)?.unshift(object);

		return object;
	}

	/**
	 * By revealing an object based on its identifier, it will be immediately shown in "refreshable" views, without requiring the user's prompt
	 * @param identifier The identifier for the object to reveal
	 */
	public revealAddedObject(identifier: string) {

		// Tell tracker to reveal object immediately if added:
		this.revealedObjectIds.push(identifier);

		// If already added, reveal it now, by unsetting the hidden "$new" property:
		for (const map of this.maps) {
			map.objectMap.get(identifier)?.forEach((objectInMemory) => {
				objectInMemory.$new = false;
			});
		}
	}

	private getMap(context: TrackingContext) {
		let map = this.maps.find(map => map.context === context);
		if (!map) {
			map = new TrackedDataMap<T, TMessageData>(context);
			this.maps.push(map);
			context.onDestroy(() => {
				map?.trackedRefreshableArrays.forEach(trackedArray => trackedArray.refreshSubscription.unsubscribe());
				this.maps.splice(this.maps.indexOf(map!), 1);
			});
		}
		return map;
	}

	

	private async insertModels(insertedObject: T, eventData: TMessageData, revealInsertedObjects?: boolean, callback?: (dataObject: T, trackingContext: TrackingContext) => Promise<void>) {
		let insertedObjectNeedsUpdate = false;
		for (const map of this.maps) {
			let trackedObjectInCurrentContext = false;
			for (const trackedArray of map.trackedRefreshableArrays) {

				if (!trackedArray.filterForInserts(eventData)) 
					continue;

				// Ensure insertedObject is not already in array, if so then update it instead of inserting it
				if (trackedArray.objects.find(o => this.getIdentifier(o) == this.getIdentifier(insertedObject))) {
					insertedObjectNeedsUpdate = true;
					continue;
				}

				if (!trackedObjectInCurrentContext) {
					if (!insertedObjectNeedsUpdate) // No need to make callback if we are updating the object, as callback is then called multiple times
						await callback?.(insertedObject, map.context); 
					this.trackObjectForComponent(map.context, insertedObject);
					trackedObjectInCurrentContext = true;
				}

				// Set object to "$new" object was not set to be revealed immediately
				// Tells refreshable views to hide it
				if (!revealInsertedObjects && this.revealedObjectIds.findIndex(id => id == this.getIdentifier(insertedObject)) == -1) {
					trackedArray.refreshService.refreshAvailable();
					insertedObject.$new = true;
				}

				trackedArray.objects.push(insertedObject);
			}
		}
		if (insertedObjectNeedsUpdate)
			this.updateMatchingModels(this.getIdentifier(insertedObject), insertedObject, callback);
	}

	private removeMatchingModels(identifier: string, hideRemovedObjects: boolean) {
		for (const map of this.maps) {
			map.objectMap.get(identifier)?.forEach((objectInMemory) => {

				const trackedArrays = map.trackedRefreshableArrays.filter(array => array.objects.indexOf(objectInMemory) >= 0);
				if (hideRemovedObjects) {
					// Remove object from object lists
					for (const objectList of trackedArrays.map(a => a.objects)) {
						objectList.splice(objectList.indexOf(objectInMemory), 1);
					}
				}
				else {
					// Just mark object as deleted, and notify that refresh is available
					objectInMemory.$deleted = true;
					for (const trackedArray of trackedArrays) {
						trackedArray.refreshService.refreshAvailable();
					}
				}
				
			});
			map.objectMap.delete(identifier);
		}
	}

	private updateMatchingModels(identifier: string, updatedObject: T, callback?: (dataObject: T, trackingContext: TrackingContext) => Promise<void>) {
		for (const map of this.maps) {
			map.objectMap.get(identifier)?.forEach(async (objectInMemory) => {

				this.referenceFix(updatedObject, map);
				for (const property of updatedObject.getProperties()) {
					(objectInMemory as any)[property.name] = (updatedObject as any)[property.name];
				}
				await callback?.(objectInMemory, map.context);
			});
		}
	}

	/**
	 * Fixes references to objects already in-memory for the data tracker
	 */
	private referenceFix(updatedObject: T, map: TrackedDataMap<T, TMessageData>) {
		for (const key in updatedObject) {
			if (!updatedObject.hasOwnProperty(key)) continue;
			const value = updatedObject[key];
			if (value && Object.getPrototypeOf(value) === Object.getPrototypeOf(updatedObject)) {
				let valueInMemory = map.objectMap.get(this.getIdentifier(value as T))?.[0];
				if (!valueInMemory) {
					this.trackObjectForComponent(map.context, value as T);
					valueInMemory = value as T;
				}
				updatedObject[key] = valueInMemory as any;
			}
			else if (Array.isArray(value)) {
				for (let i = 0; i < value.length; i++) {
					if (Object.getPrototypeOf(value[i]) !== Object.getPrototypeOf(updatedObject)) continue;
					let valueInMemory = map.objectMap.get(this.getIdentifier(value[i] as T))?.[0];
					if (!valueInMemory) {
						this.trackObjectForComponent(map.context, value[i] as T);
						valueInMemory = value[i] as T;
					}
					value[i] = valueInMemory;
				}
			}
		}
	}
}
